// Money at Risk — super_admin. Every currently-uncertain amount in one
// view: unpriced billing, undecided reship charges, refunds owed,
// uncollected payments, and long-transit parcels. Every tile opens the
// orders behind it (shared drill modal) and exports to CSV.

import { useEffect, useState } from 'react';
import { moneyAtRiskApi } from '../api/services';
import InfoTip from '../components/InfoTip';
import DrillModal, { useDrill } from '../components/DrillModal';
import toast from 'react-hot-toast';
import { Loader2, ShieldAlert, RefreshCcw } from 'lucide-react';

function fmtInr(n) {
  if (n == null || isNaN(n)) return '—';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}
function fmtUsd(n) {
  if (n == null || isNaN(n)) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

const TONES = {
  alert: 'border-rose-300 bg-rose-50/60',
  warn:  'border-amber-300 bg-amber-50/60',
  ok:    'border-emerald-200 bg-emerald-50/40',
};

// Plain-English explanation per risk item — what it measures and why it
// matters, shown via the "i" icon on each tile.
const ITEM_INFO = {
  unpriced_lines_mtd: 'Product lines dispatched this month whose product has no price on file for the dispatch date. They add ₹0 in Vendor Payables, so the month’s total is UNDERSTATED until Kalpana prices them (Vendors → Import prices). Fix these before settling any vendor.',
  needs_review_reships: '"Wrong product" reships where the vendor decides the product charge case by case. Until agreed, they bill ₹0; the amount shown is the worst case if charged at full price. Confirm with the vendor before settling.',
  refund_pending: 'Orders marked "refund pending" — the customer is waiting for money back. The $ figure is the MBO order value. Money actually moves outside Dispatch; this tracks that it hasn’t been marked done.',
  delivered_unpaid: 'Orders delivered to the customer where payment has not been collected yet (payment link sent, no response, etc.). The $ figure is the MBO order value at stake.',
  transit_over_30d: 'Parcels India Post still shows as moving after 30 days. Most of these end as loss claims or reships — chasing India Post now is cheaper than a reship later. Days count from when the tracking number was recorded.',
};

export default function MoneyAtRisk() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { drill, drillData, drillLoading, openDrill, exportDrill, closeDrill } = useDrill();

  function load() {
    setLoading(true);
    moneyAtRiskApi.build()
      .then((r) => setData(r.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  const openItems = data ? data.items.filter((i) => i.count > 0) : [];
  const cleanItems = data ? data.items.filter((i) => i.count === 0) : [];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-rose-600" />
          <h1 className="text-2xl font-semibold text-gray-900">Money at Risk</h1>
          <InfoTip title="What this page shows" align="left">
            Every amount that is currently <b>uncertain</b> — money we might
            be under-billing, money owed to customers, money customers owe
            us, and parcels likely to turn into reship costs. Click any item
            for the exact orders behind it; every list exports to CSV so you
            can send it to the right person. A clean page means the month's
            numbers can be trusted.
          </InfoTip>
        </div>
        <button onClick={load} disabled={loading}
                className="flex items-center gap-1 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Everything uncertain about this month's money, in one place. Empty is good.
      </p>

      {loading && !data && (
        <div className="py-16 text-center text-sm text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Checking…
        </div>
      )}

      {data && (
        <>
          {openItems.length === 0 && (
            <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-6 text-center text-sm text-emerald-800 mb-4">
              Nothing at risk right now — every dispatched line is priced, no reship
              charges are undecided, no refunds or collections are pending, and no
              parcel has been in transit over 30 days.
            </div>
          )}

          <div className="space-y-3">
            {openItems.map((it) => (
              <div key={it.key}
                   className={`rounded-xl border p-4 hover:ring-2 hover:ring-blue-300 ${TONES[it.severity]}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => openDrill(it.key)} title="See which orders these are"
                              className="text-sm font-semibold text-gray-900 hover:underline text-left">
                        {it.label}
                      </button>
                      <InfoTip title={it.label}>{ITEM_INFO[it.key]}</InfoTip>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">{it.detail}</div>
                  </div>
                  <button onClick={() => openDrill(it.key)} title="See which orders these are"
                          className="text-right shrink-0 hover:underline">
                    <div className="text-2xl font-semibold tabular-nums text-gray-900">{it.count}</div>
                    <div className="text-xs text-gray-600">
                      {it.amountInr != null && it.amountInr > 0 && fmtInr(it.amountInr)}
                      {it.amountUsd != null && it.amountUsd > 0 && fmtUsd(it.amountUsd)}
                    </div>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {cleanItems.length > 0 && openItems.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-2">All clear</div>
              <div className="flex flex-wrap gap-2">
                {cleanItems.map((it) => (
                  <span key={it.key}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-emerald-50 text-emerald-700 border border-emerald-200">
                    ✓ {it.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          <p className="text-[11px] text-gray-400 mt-4">As of {data.asOf} · billing risks measured over {data.monthStart} → {data.asOf}</p>
        </>
      )}

      {drill && (
        <DrillModal data={drillData} loading={drillLoading}
                    onClose={closeDrill} onExport={exportDrill} />
      )}
    </div>
  );
}
